import React, { useState } from 'react';
import { INIKRU_LOGO as inikruLogo } from '../utils/brandAssets';
import { Link } from 'react-router-dom';
import { FaInstagram, FaXTwitter, FaFacebookF, FaLinkedinIn, FaPinterestP } from 'react-icons/fa6';
import { Send, ArrowUp } from 'lucide-react';
import ScrollAnimate, { fadeInUp, staggerContainer } from './ScrollAnimate';
import API_BASE_URL from '../config/api';

// Reusable Footer Column Component for animation
const FooterColumn = ({ title, children }) => (
  <ScrollAnimate variants={fadeInUp} tag="div">
    <h3 className="mb-4 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500">
      {title}
    </h3>
    <ul className="space-y-2.5 text-[14px] font-medium text-gray-300">
      {children}
    </ul>
  </ScrollAnimate>
);

// Reusable Footer Link Item
const FooterLink = ({ to, children, onClick }) => (
  <li>
    <Link
      to={to}
      onClick={onClick}
      className="inline-block rounded transition-colors duration-200 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A]"
    >
      {children}
    </Link>
  </li>
);

export default function VFooter() {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState(null); // null | 'success' | 'error'

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubscribing(true);
    // Records the signup for real via the contact endpoint (was a mock that
    // showed a native "Thank you" alert while saving nothing).
    try {
      await fetch(`${API_BASE_URL}/api/contact_us/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'Newsletter signup',
          email,
          subject: 'Newsletter signup',
          message: `Please add ${email} to the INIKRU newsletter list.`,
        }),
      });
      setSubscribeStatus('success');
      setEmail('');
    } catch (err) {
      setSubscribeStatus('error');
    } finally {
      setIsSubscribing(false);
    }
  };

  const socialLinks = [
    { href: "https://www.instagram.com/inikru/", icon: <FaInstagram />, label: "Instagram" },
    { href: "https://x.com/inikru", icon: <FaXTwitter />, label: "X/Twitter" },
    { href: "https://www.facebook.com/inikru", icon: <FaFacebookF />, label: "Facebook" },
    { href: "https://www.linkedin.com/company/inikru/", icon: <FaLinkedinIn />, label: "LinkedIn" },
    { href: "https://in.pinterest.com/inikru/", icon: <FaPinterestP />, label: "Pinterest" },
  ];

  return (
    <footer className="bg-ink px-4 pt-14 pb-8 text-white sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Brand row */}
        <ScrollAnimate variants={fadeInUp} tag="div" className="mb-10">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              onClick={scrollToTop}
              aria-label="Inikru home"
              className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A]"
            >
              <img src={inikruLogo} alt="Inikru" className="h-[26px] w-auto block" />
            </Link>
            {/* The logo's hanging OPEN sign */}
            <span className="badge-open">Open</span>
          </div>
          <p className="mt-3 max-w-sm text-[14px] leading-relaxed text-gray-400">
            Your own branded online store — products, payments, orders and customers, all under your name.
          </p>
        </ScrollAnimate>

        <ScrollAnimate
          variants={staggerContainer(0.2)}
          className="grid grid-cols-1 gap-8 text-left sm:grid-cols-2 lg:grid-cols-4 lg:gap-12 mb-8"
          tag="div"
        >
          {/* Company Section */}
          <FooterColumn title="Company">
            <FooterLink to="/" onClick={scrollToTop}>About Us</FooterLink>
            <FooterLink to="/contactus" onClick={scrollToTop}>Contact Us</FooterLink>
          </FooterColumn>

          {/* Policies Section */}
          <FooterColumn title="Policies">
            <FooterLink to="/privacyPolicy" onClick={scrollToTop}>Privacy Policy</FooterLink>
            <FooterLink to="/TermsNConditions" onClick={scrollToTop}>Terms of Service</FooterLink>
            <FooterLink to="/rtnPolicy" onClick={scrollToTop}>Exchange Policy</FooterLink>
          </FooterColumn>

          {/* Support Section */}
          <FooterColumn title="Support">
            <FooterLink to="/payShip" onClick={scrollToTop}>Shipping & Payments</FooterLink>
            <FooterLink to="/Faqs" onClick={scrollToTop}>FAQ's</FooterLink>
          </FooterColumn>

          {/* Newsletter & Social Section */}
          <ScrollAnimate variants={fadeInUp} tag="div">
            <h3 className="mb-4 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500">
              Stay Connected
            </h3>

            {/* Newsletter Form */}
            <form onSubmit={handleNewsletterSubmit} className="mb-6">
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 w-full flex-1 rounded-[10px] border-[1.5px] border-white/20 bg-white/[0.06] px-4 text-[14px] text-white outline-none transition-colors placeholder:text-gray-500 focus:border-white/60 focus:ring-2 focus:ring-[#B40A0A]"
                />
                <button
                  type="submit"
                  disabled={isSubscribing}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-[10px] bg-white px-5 font-display text-[13.5px] font-bold text-ink transition-colors hover:bg-cream disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A] sm:w-auto w-full"
                >
                  {isSubscribing ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-ink/30 border-t-ink" />
                  ) : (
                    <Send size={15} />
                  )}
                  Subscribe
                </button>
              </div>
              {subscribeStatus === 'success' && (
                <p className="mt-2 text-sm font-semibold text-emerald-400">Thank you for subscribing!</p>
              )}
              {subscribeStatus === 'error' && (
                <p className="mt-2 text-sm font-semibold text-red-400">Could not subscribe right now. Please try again.</p>
              )}
            </form>

            {/* Social Icons */}
            <div className="flex space-x-4 text-xl">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="rounded text-gray-400 transition-colors duration-200 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A]"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </ScrollAnimate>
        </ScrollAnimate>

        <ScrollAnimate variants={fadeInUp} tag="div">
          <hr className="my-6 border-white/10 md:my-8" />
        </ScrollAnimate>

        <ScrollAnimate variants={fadeInUp} tag="div" className="text-center text-xs font-medium text-gray-400 sm:text-sm">
          <p className="mb-4">
            © {new Date().getFullYear()} Inikru. All rights reserved.
          </p>
          <button
            onClick={scrollToTop}
            className="inline-flex items-center gap-2 rounded-[10px] px-4 py-2 font-display font-bold text-gray-300 transition-colors duration-200 hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B40A0A]"
            aria-label="Back to top"
          >
            <span>Back to Top</span>
            <ArrowUp size={16} />
          </button>
        </ScrollAnimate>
      </div>
    </footer>
  );
}
